import React from 'react';
import classNames from 'classnames';
import BLANK_MEDIUM_IMAGE from '../../images/products/blank_medium.png';

import bind from '../bind';

import {
  YOUTUBE_IMAGE,
  YOUTUBE_IMAGE_MAXRES,
  YOUTUBE_IMAGE_MEDIUM,
  YOUTUBE_PLACEHOLDER_MAX_WIDTH,
  YOUTUBE_VIDEO,
} from '../url_helpers';
import { RCImage } from '@reverbdotcom/cadence/components';

const ASPECT_MAX = 1;
const ASPECT_MIN = 0.7;

interface IProps {
  index: number;
  urlOrSlug: string;
  alt?: string;
  onClick?: any;
  onLoad?: any;
  controls?: any;
  renderVideo?: boolean;
  aspectOverride?: number;
  eagerLoad?: boolean;
  disableImageOverlay?: boolean;
  imageOverlay?: 'offwhite' | 'offwhiteWithHover';
  // shows a small video poster, for thumbnail strips
  thumbnail?: boolean;
  // fills its parent and contain-fits the image, instead of sizing itself to an aspect ratio
  contain?: boolean;
}

interface IState {
  hasError: boolean;
  maxresMissing: boolean;
}

export default class LightboxImageItem extends React.Component<IProps, IState> {
  state: IState = { hasError: false, maxresMissing: false };

  componentDidUpdate(prevProps: IProps) {
    if ((this.state.hasError || this.state.maxresMissing) && prevProps.urlOrSlug !== this.props.urlOrSlug) {
      this.setState({ hasError: false, maxresMissing: false });
    }
  }

  isTryingMaxres() {
    return !this.isLink(this.props.urlOrSlug) && !this.props.thumbnail && !this.state.maxresMissing;
  }

  @bind
  handleImageError() {
    if (this.isTryingMaxres()) {
      this.setState({ maxresMissing: true });
      return;
    }

    if (!this.state.hasError) {
      this.setState({ hasError: true });
    }
  }

  @bind
  handleImageLoad(evt: React.SyntheticEvent<HTMLImageElement>) {
    if (this.isTryingMaxres() && evt.currentTarget.naturalWidth <= YOUTUBE_PLACEHOLDER_MAX_WIDTH) {
      this.setState({ maxresMissing: true });
      return;
    }

    this.props.onLoad?.(evt);
  }

  isLink(url) {
    if (!url) { return false; }
    return url.slice(0, 4) === 'http';
  }

  getDisplaySrc() {
    return this.state.hasError ? BLANK_MEDIUM_IMAGE : this.getImageURL();
  }

  getImageURL() {
    const { urlOrSlug } = this.props;
    if (this.isLink(urlOrSlug)) return urlOrSlug;
    if (this.props.thumbnail) return YOUTUBE_IMAGE_MEDIUM.expand({ slug: urlOrSlug });
    if (this.state.maxresMissing) return YOUTUBE_IMAGE.expand({ slug: urlOrSlug });
    return YOUTUBE_IMAGE_MAXRES.expand({ slug: urlOrSlug });
  }

  @bind
  handleClick(evt) {
    if (!this.props.onClick) return;
    this.props.onClick(evt, this.props.index);
  }

  getAspectOverride() {
    const a = this.props.aspectOverride;
    if (!a) return false;
    if (a > ASPECT_MAX) return ASPECT_MAX;
    if (a < ASPECT_MIN) return ASPECT_MIN;
    return a;
  }

  getAspectOverrideStyle() {
    const aspect = this.getAspectOverride();
    if (!aspect) return;

    return {
      style: {
        paddingBottom: `${aspect * 100}%`,
      },
    };
  }

  onLoad() {
    this.props.onLoad();
  }

  imageOverlay() {
    if (this.props.disableImageOverlay) return null;

    return this.props.imageOverlay ?? 'offwhite';
  }

  render() {
    const isVideo = !this.isLink(this.props.urlOrSlug);

    const classes = classNames(
      'lightbox-image-item',
      { 'lightbox-image-item--video': isVideo },
      { 'lightbox-image-item--contain': this.props.contain },
    );

    const slug = this.props.urlOrSlug;

    if (this.props.renderVideo) {
      return (
        // TODO – may need larger refactor to enable keyboard interaction
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={classNames(
            'lightbox-image-item',
            'lightbox-image-item--playing',
            { 'lightbox-image-item--with-overlay': !!this.imageOverlay() },
            { 'lightbox-image-item--contain': this.props.contain },
          )}
          onClick={this.handleClick}
          {...this.getAspectOverrideStyle()}
        >
          <div className="lightbox-image-item__video-frame">
            <iframe
              src={YOUTUBE_VIDEO.expand({ slug, autoplay: true })}
              title={this.props.alt}
              frameBorder="0"
              allowFullScreen
            />
          </div>
          {this.props.controls}
        </div>
      );
    }

    if (isVideo) {
      return (
        // TODO – may need larger refactor to enable keyboard interaction
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={classes}
          itemProp="videoObject"
          itemScope
          itemType="http://schema.org/VideoObject"
          onClick={this.handleClick}
          {...this.getAspectOverrideStyle()}
        >
          <meta
            itemProp="embedUrl"
            content={ YOUTUBE_VIDEO.expand({ slug })}
          />
          <RCImage
            src={this.getDisplaySrc()}
            alt={this.props.alt}
            onLoad={this.handleImageLoad}
            onError={this.handleImageError}
            eagerLoad={this.props.eagerLoad}
            overlay={this.imageOverlay()}
          />
          {this.props.controls}
        </div>
      );
    }

    return (
      // TODO – may needs larger refactor to enable keyboard interaction
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        className={classes}
        onClick={this.handleClick}
        {...this.getAspectOverrideStyle()}
      >
        <RCImage
          src={this.getDisplaySrc()}
          alt={this.props.alt}
          onLoad={this.props.onLoad}
          onError={this.handleImageError}
          eagerLoad={this.props.eagerLoad}
          overlay={this.imageOverlay()}
        />
        {this.props.controls}
      </div>
    );
  }
}
